/*
 * ET - PDP Spec Strip (sections/et-pdp-spec-strip.liquid)
 * <et-pdp-spec-strip-slider>: the specs are a static row (desktop) or two-column grid (tablet) from 750px and a Swiper
 * slider below 750px (one spec per slide, 24px gap, bullets in the shared .et-slider-dots element when shown).
 * Optional autoplay (data-autoplay / data-autoplay-delay from the section settings) rewinds to the first spec after the
 * last one and is skipped for prefers-reduced-motion. It stops for good once a visitor interacts (swipe, dot click, hover)
 * or moves keyboard focus into the slider, so moving content can always be paused (WCAG 2.2.2).
 * Swiper is created/destroyed on the breakpoint change and destroyed on disconnect, so theme editor re-renders
 * (shopify:section:load / unload) never leave a duplicate instance. Blocks selected in the editor slide into view.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-pdp-spec-strip-slider')) return;

  const SLIDER_QUERY = '(max-width: 749px)';
  const SLIDE_GAP = 24;
  const SLIDE_SELECTOR = '[data-et-pdp-spec-strip-slide]';

  class EtPdpSpecStripSlider extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.loading = false;
      this.pendingIndex = null;
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
    }

    connectedCallback() {
      this.addEventListener('focusin', this.handleFocusIn);
      this.media = window.matchMedia(SLIDER_QUERY);
      if (typeof this.media.addEventListener === 'function') {
        this.media.addEventListener('change', this.handleMediaChange);
      } else if (typeof this.media.addListener === 'function') {
        this.media.addListener(this.handleMediaChange);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.handleMediaChange, { once: true });
      } else {
        this.handleMediaChange();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.handleFocusIn);
      if (this.media) {
        if (typeof this.media.removeEventListener === 'function') {
          this.media.removeEventListener('change', this.handleMediaChange);
        } else if (typeof this.media.removeListener === 'function') {
          this.media.removeListener(this.handleMediaChange);
        }
      }
      document.removeEventListener('DOMContentLoaded', this.handleMediaChange);
      this.destroy();
    }

    handleMediaChange() {
      if (!this.isConnected) return;
      if (this.media && this.media.matches) this.init();
      else this.destroy();
    }

    init() {
      const swiperEl = this.querySelector('[data-et-pdp-spec-strip-swiper]');
      if (!swiperEl || this.swiper || this.loading || typeof window.etLoadSwiper !== 'function') return;
      if (!this.media || !this.media.matches) return;
      if (swiperEl.querySelectorAll(SLIDE_SELECTOR).length < 2) return;

      this.loading = true;
      window.etLoadSwiper().then((Swiper) => {
        this.loading = false;
        if (!Swiper || !this.isConnected || this.swiper || !this.media.matches) return;

        const dotsEl = this.querySelector('[data-et-pdp-spec-strip-dots]');
        const row = swiperEl.querySelector('.swiper-wrapper');
        if (row) row.scrollLeft = 0;

        const autoplay = this.autoplayOptions();

        this.swiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: SLIDE_GAP,
          speed: 400,
          watchOverflow: true,
          grabCursor: true,
          rewind: Boolean(autoplay),
          autoplay,
          pagination: dotsEl ? { el: dotsEl, clickable: true } : false,
          a11y: { enabled: true }
        });

        if (this.pendingIndex !== null) {
          if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
          this.swiper.slideTo(this.pendingIndex, 0);
          this.pendingIndex = null;
        }
      });
    }

    autoplayOptions() {
      if (this.dataset.autoplay !== 'true') return false;
      const reducedMotion =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) return false;
      const delay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
      return { delay, disableOnInteraction: true, pauseOnMouseEnter: true };
    }

    handleFocusIn() {
      if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
    }

    destroy() {
      this.pendingIndex = null;
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      const dotsEl = this.querySelector('[data-et-pdp-spec-strip-dots]');
      if (dotsEl) dotsEl.innerHTML = '';
    }

    selectSlide(slide) {
      const slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
      const index = slides.indexOf(slide);
      if (index < 0) return;

      if (this.swiper) {
        if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
        this.swiper.slideTo(index, 0);
      } else if (this.media && this.media.matches) {
        this.pendingIndex = index;
      }
    }

    resumeAutoplay() {
      if (this.swiper && this.swiper.params.autoplay && this.swiper.params.autoplay.enabled && this.swiper.autoplay) {
        this.swiper.autoplay.start();
      }
    }
  }

  window.customElements.define('et-pdp-spec-strip-slider', EtPdpSpecStripSlider);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-pdp-spec-strip-slider')) : [];

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.handleMediaChange()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const slider = slide && slide.closest('et-pdp-spec-strip-slider');
    if (slider) slider.selectSlide(slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slider = event.target instanceof Element ? event.target.closest('et-pdp-spec-strip-slider') : null;
    if (slider) slider.resumeAutoplay();
  });
})();
